import { useMemo, useRef } from "react";
import { ItemKey } from "./types";

type useScopedSelectAllArgs<T> = {
  visibleItems: T[];
  selectedKeys: ItemKey[];
  onChange: (keys: ItemKey[]) => void;
  getKey: (item: T) => ItemKey;
};

export function useScopedSelectAll<T>({
  visibleItems,
  selectedKeys,
  onChange,
  getKey,
}: useScopedSelectAllArgs<T>) {
  const selectAllRef = useRef<HTMLInputElement | null>(null);

  const visibleKeys = useMemo(
    () => visibleItems.map(getKey),
    [visibleItems, getKey],
  );

  const selectedSet = useMemo(() => new Set(selectedKeys), [selectedKeys]);

  const selectedCountInScope = useMemo(() => {
    let count = 0;
    for (const k of visibleKeys) {
      if (selectedSet.has(k)) count++;
    }
    return count;
  }, [visibleKeys, selectedSet]);

  const allSelected =
    visibleKeys.length > 0 &&
    selectedCountInScope === visibleKeys.length;

  /* Reported, not applied: SelectAllToolbar owns the checkbox. */
  const someSelected = selectedCountInScope > 0 && !allSelected;

  const setAllSelected = (checked: boolean) => {
    if (checked) {
      onChange(Array.from(new Set([...selectedKeys, ...visibleKeys])));
    } else {
      const visibleSet = new Set(visibleKeys);
      onChange(selectedKeys.filter((k) => !visibleSet.has(k)));
    }
  };

  return {
    selectAllRef,
    allSelected,
    someSelected,
    selectedCountInScope,
    setAllSelected,
  };
}
